import { useEffect } from 'react';
import {
  FlaskConical, Palette, Trophy, Monitor, Globe, Brain,
  ArrowRight, MapPin, Phone, Mail, TrendingUp, Users, Award, Clock
} from 'lucide-react';

const GF = 'https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap';

const FEATURES = [
  { icon: FlaskConical, title: 'STEM Excellence',        body: 'Fully equipped science labs with project-based learning aligned to CBSE standards.', highlight: true },
  { icon: Palette,      title: 'Creative Arts',          body: 'Music, visual arts, theatre, and design with dedicated studios and professional mentors.', highlight: false },
  { icon: Trophy,       title: 'Sports Academy',         body: 'Modern facilities for cricket, football, swimming, athletics, and 12+ other sports.', highlight: false },
  { icon: Monitor,      title: 'Digital Learning',       body: 'Smart classrooms, digital library, AI literacy courses, and 1:1 device programme.', highlight: false },
  { icon: Globe,        title: 'Global Exposure',        body: 'International exchange programmes, Model UN, and partnerships with schools in 12 countries.', highlight: false },
  { icon: Brain,        title: 'Counselling & Wellness', body: 'Dedicated mental health counsellors, mindfulness, and a student wellness centre.', highlight: false },
];

const PROGRAMS = [
  { grade: 'Nursery–KG',   name: 'Early Childhood',          desc: 'Play-based learning with Montessori-inspired methods' },
  { grade: 'Grade I–V',    name: 'Primary School',           desc: 'Foundation years with holistic development focus' },
  { grade: 'Grade VI–VIII',name: 'Middle School',            desc: 'Critical thinking, project work, and exploration' },
  { grade: 'Grade IX–X',   name: 'Secondary (CBSE)',         desc: 'Board preparation with robust academic support' },
  { grade: 'Grade XI–XII', name: 'Senior Secondary',         desc: 'Science, Commerce & Humanities with career counselling' },
];

export default function ModernTheme({ school }) {
  const loginUrl = `/login?school=${school.slug}`;

  useEffect(() => {
    const link = Object.assign(document.createElement('link'), { rel: 'stylesheet', href: GF });
    document.head.appendChild(link);
    document.title = `${school.name} | EduPulse`;
    return () => document.head.removeChild(link);
  }, [school.name]);

  const MARQUEE_ITEMS = ['Academic Excellence', `${school.board} Affiliated`, 'Smart Classrooms', 'STEM Labs', 'Sports Academy', 'Cultural Fests', 'Digital Library'];

  return (
    <div className="font-jakarta bg-white text-black overflow-x-hidden">

      {/* ── NAV ── */}
      <nav className="fixed top-0 inset-x-0 z-50 bg-white/92 backdrop-blur-xl border-b border-black/6 h-[68px] flex items-center justify-between px-12">
        <a href="/" className="flex items-center gap-2.5 no-underline">
          <div className="w-9 h-9 bg-brand-green rounded-[10px] flex items-center justify-center font-syne font-extrabold text-base text-white">
            {school.name[0]}
          </div>
          <span className="font-syne font-bold text-[17px] text-black">{school.name.split(' ')[0]}</span>
        </a>
        <ul className="hidden md:flex gap-8 list-none">
          {['About', 'Academics', 'Activities', 'Contact'].map(l => (
            <li key={l}>
              <a href={`#${l.toLowerCase()}`} className="text-[14px] font-medium text-[#6e6e6e] hover:text-black transition-colors no-underline">{l}</a>
            </li>
          ))}
        </ul>
        <a href={loginUrl}
          className="text-[14px] font-bold text-black bg-brand-green px-5 py-2.5 rounded-[10px] hover:bg-brand-green-dark hover:text-white transition-all no-underline">
          Login {new Date().getFullYear()}
        </a>
      </nav>

      {/* ── HERO ── */}
      <section className="min-h-screen bg-black px-12 pt-36 pb-20 flex flex-col justify-center relative overflow-hidden">
        <div className="absolute top-1/2 left-12 -translate-y-1/2 font-syne font-extrabold text-[22vw] text-white/[0.03] leading-none select-none pointer-events-none whitespace-nowrap">
          LEARN
        </div>

        {/* live chip */}
        <div className="inline-flex items-center gap-2 bg-brand-green/15 border border-brand-green/30 text-brand-green text-[13px] font-semibold px-3.5 py-1.5 rounded-full mb-8 w-fit">
          <span className="w-[7px] h-[7px] bg-brand-green rounded-full animate-pulse-dot" />
          Now Accepting Applications · {new Date().getFullYear()}–{String(new Date().getFullYear() + 1).slice(2)}
        </div>

        <h1 className="font-syne font-extrabold text-[clamp(52px,6.5vw,88px)] leading-[1.0] text-white mb-7 max-w-3xl relative z-10">
          Where <span className="text-brand-green">Great</span><br />
          Futures<br />
          <span className="relative inline-block">
            Begin.
            <span className="absolute bottom-1 left-0 right-0 h-1 bg-yellow-300 rounded" />
          </span>
        </h1>

        <p className="text-[18px] text-white/55 leading-[1.7] max-w-lg mb-12 font-light relative z-10">
          {school.name} is redefining education for the 21st century — blending academic
          rigour with creativity, technology, and real-world readiness.
        </p>

        <div className="flex gap-3.5 items-center mb-20 relative z-10 flex-wrap">
          <a href={loginUrl}
            className="inline-flex items-center gap-2 font-bold text-[15px] bg-brand-green text-black px-8 py-4 rounded-xl hover:bg-brand-green-dark hover:text-white transition-all hover:-translate-y-0.5 no-underline">
            Login to Portal <ArrowRight size={16} />
          </a>
          <a href="#about"
            className="text-[15px] font-semibold text-white/70 border border-white/15 px-8 py-4 rounded-xl hover:border-white/40 hover:text-white transition-all no-underline">
            Learn More
          </a>
        </div>

        <div className="flex gap-14 relative z-10 flex-wrap">
          {[
            { num: school.stats.students, label: 'Students' },
            { num: school.stats.results, label: 'Board Results' },
            { num: `${school.stats.staff}+`, label: 'Faculty Members' },
          ].map((s, i) => (
            <div key={i}>
              <div className="font-syne font-extrabold text-[36px] text-white leading-none">
                {s.num}<span className="text-brand-green" />
              </div>
              <div className="text-[13px] text-white/40 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── MARQUEE ── */}
      <div className="bg-brand-green py-4 overflow-hidden whitespace-nowrap">
        <div className="inline-block animate-marquee">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span key={i} className="font-syne font-bold text-[13px] tracking-[0.1em] uppercase text-black px-8 after:content-['✦'] after:ml-8 after:text-black/30" >
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* ── FEATURES ── */}
      <section className="py-24 px-12" id="about">
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
          <h2 className="font-syne font-extrabold text-[clamp(36px,3.5vw,50px)] leading-[1.1] max-w-md">
            Built for the <span className="text-brand-green">next generation</span> of leaders
          </h2>
          <p className="text-[16px] text-[#6e6e6e] leading-[1.7] max-w-xs">
            Every programme and activity is intentionally designed for modern learners.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {FEATURES.map(({ icon: Icon, title, body, highlight }) => (
            <div key={title}
              className={`rounded-[20px] p-9 transition-all duration-300 group cursor-default
                ${highlight ? 'bg-brand-green' : 'bg-[#f4f4f4] hover:bg-black'}`}>
              <div className={`w-[52px] h-[52px] rounded-[14px] flex items-center justify-center mb-6 transition-colors
                ${highlight ? 'bg-black' : 'bg-white group-hover:bg-brand-green'}`}>
                <Icon size={22} className={highlight ? 'text-brand-green' : 'text-black group-hover:text-black'} />
              </div>
              <div className={`font-syne font-bold text-[20px] mb-3 ${highlight ? 'text-black' : 'group-hover:text-white'}`}>
                {title}
              </div>
              <p className={`text-[15px] leading-[1.7] ${highlight ? 'text-black/60' : 'text-[#6e6e6e] group-hover:text-white/50'}`}>
                {body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── NUMBERS ── */}
      <section className="py-24 px-12 bg-black">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/7">
          {[
            { val: `${new Date().getFullYear() - school.establishedYear}+`, label: 'Years Established' },
            { val: school.stats.results, label: 'Board Pass Rate' },
            { val: `${school.stats.staff}+`, label: 'Faculty Members' },
            { val: school.stats.students, label: 'Students Enrolled' },
          ].map((n, i) => (
            <div key={i} className="px-10 py-10">
              <div className="font-syne font-extrabold text-[60px] text-brand-green leading-none">{n.val}</div>
              <div className="text-[14px] text-white/40 mt-2.5 font-light">{n.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── PROGRAMS ── */}
      <section className="py-24 px-12 bg-brand-green-pale" id="academics">
        <h2 className="font-syne font-extrabold text-[clamp(36px,3.5vw,50px)] mb-4">Our Programmes</h2>
        <p className="text-[17px] text-[#6e6e6e] mb-14">
          From Nursery to Grade {school.academic?.grades?.[school.academic?.grades?.length - 1]} — a seamless, world-class learning journey.
        </p>
        <div className="flex flex-col gap-0.5">
          {PROGRAMS.map((p, i) => (
            <div key={i}
              className="bg-white px-10 py-8 flex items-center justify-between gap-6 transition-all duration-200 border-l-4 border-transparent hover:border-brand-green hover:pl-11 group cursor-default
                first:rounded-t-2xl last:rounded-b-2xl">
              <div className="font-syne font-bold text-[13px] text-brand-green w-28 shrink-0">{p.grade}</div>
              <div className="font-syne font-bold text-[20px] flex-1">{p.name}</div>
              <div className="text-[14px] text-[#6e6e6e] max-w-xs text-right hidden md:block">{p.desc}</div>
              <ArrowRight size={24} className="text-[#6e6e6e] shrink-0 group-hover:text-brand-green group-hover:translate-x-1.5 transition-all" />
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-28 px-12 bg-black text-center relative overflow-hidden" id="contact">
        <div className="absolute top-[-100px] left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[radial-gradient(circle,rgba(0,196,106,0.15),transparent_70%)]" />
        <h2 className="font-syne font-extrabold text-[clamp(40px,5vw,64px)] text-white mb-5 relative">
          Ready to join <span className="text-brand-green">{school.name.split(' ')[0]}?</span>
        </h2>
        <p className="text-[18px] text-white/50 max-w-md mx-auto leading-[1.7] mb-12">
          Admissions for {new Date().getFullYear()}–{String(new Date().getFullYear() + 1).slice(2)} are open.
          Seats are limited — secure your child's future today.
        </p>
        <a href={loginUrl}
          className="inline-flex items-center gap-2 font-bold text-[15px] bg-brand-green text-black px-8 py-4 rounded-xl hover:bg-brand-green-dark hover:text-white transition-all relative no-underline">
          Login to Portal <ArrowRight size={16} />
        </a>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-[#0d0d0d] border-t border-white/6 py-20 px-12">
        <div className="grid md:grid-cols-4 gap-16 mb-16">
          <div>
            <div className="font-syne font-extrabold text-[22px] text-white mb-3">{school.name}</div>
            <p className="text-[14px] text-white/35 leading-[1.8] max-w-[260px] mb-4">
              Empowering students to think boldly, act responsibly, and lead with confidence.
            </p>
            <div className="space-y-1.5 text-[13px] text-white/30">
              <div className="flex items-start gap-2"><MapPin size={12} className="mt-0.5 shrink-0" />{school.address}</div>
              <div className="flex items-center gap-2"><Phone size={12} />{school.phone}</div>
            </div>
          </div>
          {[
            { t: 'Academics',   l: ['Curriculum', 'Timetable', 'Exams', 'Results'] },
            { t: 'School Life', l: ['Sports', 'Arts', 'Clubs', 'Events'] },
            { t: 'Quick Links', l: ['Admissions', 'Fee Structure', 'Parent Portal', 'Contact'] },
          ].map(col => (
            <div key={col.t}>
              <div className="font-syne font-bold text-[13px] text-white/50 uppercase tracking-[0.1em] mb-5">{col.t}</div>
              <ul className="space-y-3 list-none">
                {col.l.map(l => (
                  <li key={l}><a href="#contact" className="text-[14px] text-white/40 hover:text-brand-green transition-colors no-underline">{l}</a></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t border-white/6 pt-8 flex flex-col md:flex-row justify-between text-[13px] text-white/20">
          <span>© {new Date().getFullYear()} {school.name}. All rights reserved.</span>
          <span>{school.affiliation}</span>
        </div>
      </footer>
    </div>
  );
}